import {useQuery} from "@tanstack/react-query";
import {AxiosError} from "axios";
import {useClientV2} from "../../../../utils/hooks";
import {useCustomerIdentity} from "../../../../redux/auth/login/customer-identity";
import {PaginationMeta} from "../../../../redux/service/i-service";
import {buildPaginationMeta} from "../../../customers/subscriptions/shared/pagination-meta";
import {GetStoreServicesOrdersApiResponse} from "./types";
import {GET_STORE_SERVICE_CUSTOMER_ORDERS_RQ_KEY} from "../constants";

/**
 * STRANGLER BRIDGE (coach orders list)
 *
 * The coach orders list now flows through the feature module
 * (`features/product-services`), which always uses the v2 implementation.
 * It preserves the params, response shape, query key, and refetch
 * behavior, so consumers need no changes.
 *
 * @deprecated Use `useProductServiceOrders` from `features/product-services`.
 */
export {useGetStoreServicesOrders} from "../../../../features/product-services/hooks/useGetStoreServicesOrders";

type LegacyCustomerOrder = GetStoreServicesOrdersApiResponse["orders"][number];

/** A row of `GET customer/product-services/orders` (v2). */
type V2CustomerOrderRow = {
  id: number;
  productServiceId: number;
  statusId: number;
  statusName: string;
  currency: string;
  amount: number;
  deliveryDate: string | null;
  createdAt: string;
  updatedAt: string;
  productServiceName: string | null;
  coachDisplayName: string | null;
};

type V2CustomerOrdersEnvelope = {
  data?: {
    data?: V2CustomerOrderRow[];
    total?: number;
    hasNextPage?: boolean;
    hasPrevPage?: boolean;
  };
};

/** Identity of the signed-in customer, used for fields v2 does not return. */
type SignedInCustomer = {
  firstName: string;
  lastName: string;
  emailAddress: string;
};

/**
 * Maps a v2 customer order row onto the legacy list-item shape.
 *
 * The v2 DTO is deliberately narrow, so several legacy fields are synthesized:
 * - `productService` must be truthy — the list skips rows without it — but v2
 *   only returns the service *name* and id, so the object is stubbed around them.
 * - `firstName`/`lastName`/`emailAddress` are the *customer's* own (matching
 *   the v1 semantics these fields had) and come from the auth session, since
 *   v2 identifies the customer from the bearer token instead of echoing them.
 * - `revision_requested` is not exposed; it was a stored-but-unread flag, and
 *   the real revision gate is derived from the deliveries list.
 */
const toLegacyCustomerOrder = (
  row: V2CustomerOrderRow,
  customer: SignedInCustomer
): LegacyCustomerOrder => ({
  id: row.id,
  userId: 0,
  productServiceId: row.productServiceId,
  firstName: customer.firstName,
  lastName: customer.lastName,
  emailAddress: customer.emailAddress,
  currency: row.currency,
  amount: String(row.amount ?? 0),
  charges: "0",
  reference: "",
  processor_reference: null,
  statusId: row.statusId,
  is_paid: true,
  revision_requested: false,
  payment_channel: "paystack",
  discount_code: null,
  is_discounted: false,
  charge_bearer: "customer",
  booking_questions: null,
  delivery_date: row.deliveryDate ?? "",
  createdAt: row.createdAt,
  updatedAt: row.updatedAt,
  coachDisplayName: row.coachDisplayName ?? "",
  productService: {
    id: row.productServiceId,
    userId: 0,
    name: row.productServiceName ?? "",
    slug: "",
    tiny_url: "",
    description: "",
    delivery_timeframe_unit: "days",
    delivery_timeframe_value: 1,
    hideFromProfile: false,
    url_redirect: null,
    statusId: 0,
    request_phone_number: false,
    booking_questions: null,
    createdAt: "",
    updatedAt: "",
    deleted_at: null,
  },
});

/**
 * Customer orders list — v2 (`GET customer/product-services/orders`).
 *
 * The customer is identified by the bearer token, so no email is sent. The
 * legacy `{data: {orders}, meta}` shape, params, query key and refetch
 * behavior are preserved so consumers need no changes.
 */
export const useGetCustomerStoreServicesOrders = ({
  statusId,
  page = 1,
  per_page = 5,
}: {
  statusId: number;
  page: number;
  per_page: number;
}) => {
  const client = useClientV2();
  const customer: SignedInCustomer = useCustomerIdentity();

  return useQuery<
    {data: GetStoreServicesOrdersApiResponse; meta: PaginationMeta},
    AxiosError<{status: boolean; message: string}>
  >({
    queryKey: [GET_STORE_SERVICE_CUSTOMER_ORDERS_RQ_KEY, {statusId, page}],
    queryFn: async () => {
      // `perPage` (not `per_page`) — v2's validation pipe strips unknown params.
      const params = new URLSearchParams({
        page: String(page),
        perPage: String(per_page),
      });
      if (statusId !== -1) {
        params.set("statusId", String(statusId));
      }

      const raw = (await client(
        `customer/product-services/orders?${params}`
      )) as V2CustomerOrdersEnvelope;

      const rows = raw?.data?.data ?? [];
      const total = raw?.data?.total ?? rows.length;
      const hasNextPage = raw?.data?.hasNextPage ?? false;

      return {
        data: {orders: rows.map((row) => toLegacyCustomerOrder(row, customer))},
        meta: buildPaginationMeta(page, per_page, total, hasNextPage),
      };
    },
    refetchInterval: 300000,
    refetchOnWindowFocus: true,
  });
};
